import { Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';

@Directive({
  selector: '[appSignaturePad]',
  standalone: true,
  exportAs: 'signaturePad'
})
export class SignaturePadDirective implements OnInit {

  @Input() penColor: string = '#000000';
  @Input() penWidth: number = 2;
  @Output() signatureChange = new EventEmitter<string>();

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private isDrawing = false;
  private hasSignature = false;
  /**
   * The pointer id we've captured for the current stroke, if any. Tracked
   * so `pointerup` / `pointercancel` can release the capture cleanly and
   * so out-of-order `pointerdown`s (a second finger, a pen while touching)
   * don't confuse the state machine.
   */
  private capturedPointerId: number | null = null;

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.canvas = this.el.nativeElement as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d');
    this.ctx.strokeStyle = this.penColor;
    this.ctx.lineWidth = this.penWidth;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    // Prevent the browser's default touch gestures (scroll, pinch, tap-
    // hold context menu) from interrupting the stroke. Doing this in JS
    // rather than only CSS covers browsers whose default UA styles win
    // over `touch-action: none` in the stylesheet.
    this.canvas.style.touchAction = 'none';
  }

  /**
   * Converts a viewport-space point (mouse or touch client coords) into the
   * canvas's intrinsic drawing-buffer coordinate system. Without the scale
   * factor the pen drifts whenever CSS shrinks the canvas below its
   * declared width/height — most visibly in modal-in-modal contexts where
   * the canvas hits its `max-width: 100%` constraint. `getBoundingClientRect`
   * reports the DISPLAY box, so we divide the canvas's intrinsic dimensions
   * by that box to get how many drawing-buffer pixels each on-screen pixel
   * represents.
   */
  private toCanvasCoords(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    // Guard against a zero-sized rect (canvas rendered display:none, or
    // the modal opening mid-layout). Fall back to a 1:1 scale so a stroke
    // during that frame lands somewhere sensible rather than blowing up
    // with Infinity/NaN.
    const scaleX = rect.width > 0 ? this.canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? this.canvas.height / rect.height : 1;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  /**
   * Pointer events unify mouse, touch, and stylus. `setPointerCapture`
   * routes all subsequent pointer events for that pointer id to the
   * canvas even when the cursor leaves its bounding box — solves the
   * "cursor cuts out mid-stroke" bug the old `mouseleave` handler had
   * in modal-on-modal contexts where the effective canvas box was
   * smaller than expected and a stray movement pixel would end the
   * stroke prematurely.
   */
  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    // Ignore secondary buttons (right-click, middle-click) — only pen /
    // primary click / touch should paint. `isPrimary` also filters out
    // non-primary touches (a second finger during pinch).
    if (event.button !== 0 || !event.isPrimary) return;
    event.preventDefault();
    // Attempt capture FIRST — only record `capturedPointerId` on success.
    // If it throws (rare — detached element mid-init, browser quirk), the
    // id stays null so pointermove/up don't gate on a match with a
    // pointer we never actually captured. The stroke still works while
    // the pointer is inside canvas bounds; a stray exit just pauses it.
    try {
      this.canvas.setPointerCapture(event.pointerId);
      this.capturedPointerId = event.pointerId;
    } catch {
      // capturedPointerId stays null — pointermove's null-check lets any
      // pointerId through in that state, so drawing still starts.
    }
    this.isDrawing = true;
    const { x, y } = this.toCanvasCoords(event.clientX, event.clientY);
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.isDrawing) return;
    if (this.capturedPointerId !== null && event.pointerId !== this.capturedPointerId) return;
    event.preventDefault();
    const { x, y } = this.toCanvasCoords(event.clientX, event.clientY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.hasSignature = true;
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.isDrawing) return;
    if (this.capturedPointerId !== null && event.pointerId !== this.capturedPointerId) return;
    this.releaseCapture();
    if (this.hasSignature) {
      this.signatureChange.emit(this.canvas.toDataURL('image/png'));
    }
    this.isDrawing = false;
  }

  private releaseCapture(): void {
    if (this.capturedPointerId === null) return;
    try {
      this.canvas.releasePointerCapture(this.capturedPointerId);
    } catch {
      // Release can throw if capture was already lost (e.g. element
      // detached, browser reclaimed capture). Non-fatal — just clear
      // our own tracking.
    }
    this.capturedPointerId = null;
  }

  public clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.hasSignature = false;
    this.signatureChange.emit('');
  }

  public isEmpty(): boolean {
    return !this.hasSignature;
  }
}
